import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './ChatLogItem.styled';
import ChatBubble from '../../atoms/ChatBubble/ChatBubble';
import VHProfileImage, {
  AvatarSizes,
} from '../../atoms/VHProfileImage/VHProfileImage';
import TextLabel, { TextLabelTypes } from '../../atoms/TextLabel/TextLabel';
import ScoreKeyHit from '../../atoms/ScoreKeyHit/ScoreKeyHit';
import ChatBlock, { ChatBlockTypes } from '../ChatBlock/ChatBlock';
import { useSelector } from 'react-redux';
import { virtualHumanDataSelector } from '../../../slices/VirtualHumanSlice';
import { userSelector } from '../../../slices/UserSlice';
import { Icon } from '@virtidev/toolbox';
import AnimateHeight from 'react-animate-height';
import { getResizeImage } from '../../../../../core/src/helpers/imageResizing/getResizeImage';
import fallbackAvatar from '../../../../../../components/icons/avatar-default.jpeg';
import { useTheme } from 'styled-components';
import TextWithParsedLinks from './TextWithParsedLinks/TextWithParsedLinks';
import { useTranslation } from 'react-i18next';

const ChatLogItem = (props) => {
  const vh = useSelector(virtualHumanDataSelector);
  const user = useSelector(userSelector);

  const userAvatarSrc = useMemo(() => {
    if (user?.AvatarMedia?.TusID) {
      return getResizeImage(user?.AvatarMedia?.TusID, {
        resize: {
          width: 50,
          height: 50,
        },
      });
    } else {
      return fallbackAvatar;
    }
  }, [user?.AvatarMedia?.TusID]);

  const [feedbackActive, setFeedbackActive] = useState(true);
  const showFeedback = props.logItem?.feedbackText;
  const { color } = useTheme();
  const { t } = useTranslation();

  return (
    <Styled.Item chatType={props.logItem.chatType}>
      {props.logItem.chatType === 'bot' && (
        <VHProfileImage size={AvatarSizes.SMALL} src={vh?.ThumbnailURL} />
      )}
      {['user', 'bot'].includes(props.logItem.chatType) && (
        <Styled.ChatBubbleWrapper
          chatType={props.logItem.chatType}
          feedbackText={props.logItem.feedbackText}
        >
          <Styled.ChatNameWrapper>
            <TextLabel type={TextLabelTypes.DARK_BOLD}>
              {props.logItem.chatType === 'user' ? user?.Name : vh?.Title}
            </TextLabel>
          </Styled.ChatNameWrapper>
          <ChatBubble
            chatType={props.logItem.chatType}
            feedbackText={props.logItem.feedbackText}
          >
            <TextWithParsedLinks text={props.logItem.text ?? ''} />
            {props.logItem.isAIFallback && (
              <Styled.IsAIIconWrapper title="This was response generated by OpenAI">
                <Icon icon="brain" color={color.secondary} size="1.5rem" />
              </Styled.IsAIIconWrapper>
            )}
            {props.logItem?.scoreMetadataWithCalculated && (
              <ScoreKeyHit
                scoreMetadataWithCalculated={
                  props.logItem?.scoreMetadataWithCalculated
                }
              />
            )}
          </ChatBubble>
          {showFeedback && (
            <Styled.ChatBubbleFeedbackWrapper
              onClick={() => setFeedbackActive(!feedbackActive)}
              active={feedbackActive}
            >
              <Styled.ChatBubbleFeedbackTitleWrapper>
                <Styled.ChatBubbleFeedbackTitle>
                  {t('feedback')}
                </Styled.ChatBubbleFeedbackTitle>
                {!feedbackActive && (
                  <Styled.ChatBubbleFeedbackIconExpand>
                    <Icon
                      icon="chevron"
                      type="outline"
                      color="#fff"
                      size="1rem"
                    />
                  </Styled.ChatBubbleFeedbackIconExpand>
                )}
              </Styled.ChatBubbleFeedbackTitleWrapper>

              <AnimateHeight
                style={{
                  width: '100%',
                }}
                onAnimationEnd={() => {
                  if (feedbackActive && props.onFeedbackTextShowEnd) {
                    props.onFeedbackTextShowEnd(props.index);
                  }
                }}
                height={feedbackActive ? 'auto' : 0}
              >
                <Styled.ChatBubbleFeedbackContentWrapper>
                  <Styled.ChatBubbleFeedbackContentText
                    style={{ width: '100%' }}
                  >
                    <TextWithParsedLinks
                      contrast={true}
                      text={props.logItem.feedbackText ?? ''}
                    />
                  </Styled.ChatBubbleFeedbackContentText>
                  <Styled.ChatBubbleFeedbackIconCollapse>
                    <Icon
                      icon="chevron"
                      type="outline"
                      color="#fff"
                      size="1rem"
                    />
                  </Styled.ChatBubbleFeedbackIconCollapse>
                </Styled.ChatBubbleFeedbackContentWrapper>
              </AnimateHeight>
            </Styled.ChatBubbleFeedbackWrapper>
          )}
        </Styled.ChatBubbleWrapper>
      )}
      {props.logItem.chatType === 'user' && (
        <VHProfileImage size={AvatarSizes.SMALL} src={userAvatarSrc} />
      )}
      {props.logItem.chatType === 'diagnosis' && (
        <ChatBlock chatBlockType={ChatBlockTypes.DIAGNOSIS}>
          {props.logItem.text}
        </ChatBlock>
      )}
      {props.logItem.chatType === 'reflection-question' && (
        <ChatBlock chatBlockType={ChatBlockTypes.REFLECTION_QUESTION}>
          {props.logItem.text}
        </ChatBlock>
      )}
      {props.logItem.chatType === 'info' && (
        <ChatBlock chatBlockType={ChatBlockTypes.INFO}>
          {props.logItem.text}
        </ChatBlock>
      )}
      {props.logItem.chatType === 'feedbackText' && (
        <ChatBlock chatBlockType={ChatBlockTypes.INFO}>
          {props.logItem.text}
        </ChatBlock>
      )}
    </Styled.Item>
  );
};

ChatLogItem.propTypes = {
  logItem: PropTypes.shape({
    text: PropTypes.string.isRequired,
    chatType: PropTypes.string.isRequired,
    hitObjective: PropTypes.bool,
    points: PropTypes.number,
  }).isRequired,
  index: PropTypes.number,
  onFeedbackTextShowEnd: PropTypes.func,
};

export default ChatLogItem;
